import { useState } from "react";
import { arTargetKey } from "@greg-chick/shared";
import { compileTarget, fileToImage, imageFromUrl } from "../lib/arCompile";
import type { ManifestEntry } from "../lib/contentApi";

/**
 * 🎯 **카메라가 알아볼 그림 정하기** — 카드 사진 한 장을 「외우게」 한다.
 *
 * 전자책 쪽에도 같은 일을 하는 자리가 있는데, 거기는 **페이지 그림**을 자동으로 쓴다
 * (책은 그 그림이 곧 카드다). AR 카드는 그런 그림이 없어서 운영자가 직접 고른다 —
 * 그 차이만큼만 다르고, 외우는 일 자체는 같은 함수(`compileTarget`)를 쓴다.
 *
 * 외우기는 브라우저에서 1~2분 걸린다. 그동안 무슨 일이 벌어지는지 % 로 보여준다 —
 * 안 보여주면 운영자는 멈춘 줄 알고 창을 닫는다(그러면 처음부터 다시).
 */
interface Props {
  idKey: string;
  title: string;
  assets: Record<string, ManifestEntry>;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
  /**
   * 🔴 **이미 올려 둔 카드 그림**(포스터)의 주소. 있으면 이걸로 바로 외울 수 있다.
   *
   * 없으면 운영자는 **컴퓨터에서 원본 파일을 다시 찾아야** 한다. 그게 안 되면 그냥 안 한다 —
   * 운영에 카드 여섯 장 중 다섯 장이 몇 주째 그 상태였다(그림은 다 올려 놓고 .mind 만 없었다).
   * 책 되살리기와 같은 종류의 막힘이다: **다시 올리라고 하면 아무도 안 한다.**
   */
  posterUrl?: string;
}

export function ArTargetPicker({ idKey, title, assets, setEntry, posterUrl }: Props) {
  const targetKey = arTargetKey(idKey);
  const target = assets[targetKey];
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<{ kind: "info" | "ok" | "err"; msg: string } | null>(null);

  /**
   * 그림 한 장을 외운다.
   *
   * `from` 이 없으면 운영자가 고른 파일, 있으면 **이미 올려 둔 그림**(포스터)이다.
   * 외우는 일 자체는 같다 — 어디서 그림을 가져오는지만 다르다.
   */
  async function make(from?: string) {
    if (busy) return;
    // 조용한 무반응 금지 — 왜 안 되는지 화면에 적는다
    if (!from && !file) {
      setStatus({ kind: "err", msg: "카드 사진을 먼저 골라 주세요." });
      return;
    }
    setBusy(true);
    setProgress(0);
    let revoke: string | null = null;
    try {
      // 🔴 올려 둔 그림은 다른 출처(엣지)에 있다. `crossOrigin` 으로 받아야 픽셀을 읽을 수 있다 —
      //    안 그러면 캔버스가 오염돼 외우기가 통째로 실패한다(`imageFromUrl` 이 그걸 한다).
      const img = from ? await imageFromUrl(from) : await fileToImage(file!);
      if (!from) revoke = img.src;
      setStatus({ kind: "info", msg: "카메라가 그림을 외우는 중이에요… (1~2분, 창을 닫지 마세요)" });
      const url = await compileTarget(targetKey, img, idKey, (p) => setProgress(p));
      setEntry(targetKey, { ...(target ?? {}), url, alt: `${title} AR 타깃` });
      setFile(null);
      setStatus({ kind: "ok", msg: "✓ 다 외웠어요! 아래 [저장]을 눌러야 진짜로 켜져요." });
    } catch (e) {
      const raw = (e as Error)?.message || "";
      setStatus({
        kind: "err",
        msg: /compiler_missing|import|chunk|Failed to fetch dynamically/i.test(raw)
          ? "AR 도구를 불러오지 못했어요. 새로고침하고 다시 해보세요."
          : raw || "그림 외우기에 실패했어요. 흐릿하거나 무늬가 단순하면 다른 사진으로 해보세요.",
      });
    } finally {
      setBusy(false);
      setProgress(null);
      if (revoke) URL.revokeObjectURL(revoke);
    }
  }

  return (
    <div className="bk-ar__target-pick">
      <div className="bk-ar__target">
        <span className={`slot__badge${target?.url ? " slot__badge--real" : ""}`}>
          {target?.url ? "✓ 카메라 준비 완료" : "카메라 준비 안 됨"}
        </span>
        <label className="slot__file">
          {file ? file.name : "카드 사진 고르기"}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void make()}>
          {busy ? (progress !== null ? `외우는 중 ${progress}%…` : "준비 중…") : "이 사진을 알아보게 하기"}
        </button>
        {/* 🔴 **올려 둔 그림으로 바로.** 원본 파일을 다시 찾아오게 하지 않는다 —
            그게 안 되면 운영자는 그냥 안 한다(카드 다섯 장이 몇 주째 그랬다). */}
        {posterUrl && !target?.url ? (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy}
            onClick={() => void make(posterUrl)}
          >
            {busy ? "외우는 중…" : "🖼️ 올려 둔 카드 그림으로 하기"}
          </button>
        ) : null}
        {target?.url && !busy ? (
          <button
            type="button"
            className="slot__remove"
            onClick={() => setEntry(targetKey, { ...target, url: "" })}
          >
            준비 지우기
          </button>
        ) : null}
      </div>
      {status ? <p className={`bk-ar__status bk-ar__status--${status.kind}`}>{status.msg}</p> : null}
    </div>
  );
}
